// BASE ------------------------------------------------------------------------
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import CloseIcon from '@mui/icons-material/Close';
// MUI COMPONENTS ------------------------------------------------------------------------
import { alpha } from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/system/Box';

import CloseFullscreen from '@mui/icons-material/CloseFullscreen';
import OpeninFullRounded from '@mui/icons-material/OpenInFullRounded';

const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    position: 'absolute',
    width: '100%',
    backgroundColor: alpha(theme.palette.background.paper, 0.74),
    backdropFilter: `blur(8px)`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
    padding: '16px',
    zIndex: theme.zIndex.modal + 1,
    [theme.breakpoints.down('md')]: {
      padding: 2,
      flexWrap: 'wrap',
      justifyContent: 'space-evenly',
    },
  },
  toneSelect: {
    width: 250, // Adjust the width as needed
    '& .MuiInputBase-root': {
      fontSize: '0.875rem', // Smaller font size for the input
      padding: '0px 8px', // Smaller padding for compactness
      height: '32px', // Reduce the height of the select box
    },
    '& .MuiSelect-select': {
      padding: '4px 8px', // Adjust padding inside the select to be more compact
      minHeight: 'unset', // Remove min-height constraint
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem', // Smaller font size for the label
      top: '-6px', // Adjust the position of the label if needed
    },
    marginRight: '16px',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
    },
  },
  chevronButton: {
    transition: 'transform 0.3s ease-in-out', // Add transition for rotation
  },
  closeButton: {
    marginLeft: 'auto', // Align the close button to the right
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
}));

export default function ChatHeader({ onConvoChange, options, onClose, onExpandToggle, expandWindow = false }) {
  const { classes, theme } = useStyles();
  const isLargerThanMd = useMediaQuery('(min-width:899.95px)');
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const selects = options?.map((optionsArray) => (
    <FormControl key={`oA-${optionsArray.id}`} className={classes.toneSelect}>
      <InputLabel id={`hdr-input-label-${optionsArray.id}`}>{optionsArray.label}</InputLabel>
      <Select
        labelId={`hdr-select-label-${optionsArray.id}`}
        variant='filled'
        hiddenLabel
        id={`hdr-select-${optionsArray.id}`}
        value={optionsArray.value}
        label={optionsArray.label}
        onChange={optionsArray.onChange}
        MenuProps={{
          disablePortal: true, // Render the dropdown in the same DOM node
          PaperProps: {
            style: {
              zIndex: theme.zIndex.modal + 2,
            },
          },
        }}
      >
        {optionsArray.menuItems.map((menuItem) => (
          <MenuItem key={menuItem.value} value={menuItem.value} className={classes.menuItem}>
            {menuItem.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ));

  return (
    <Box className={classes.headerContainer}>
      {/* Chevron Icon Button for toggling width */}
      {isLargerThanMd && ( // Conditionally render the IconButton based on screen size
        <Tooltip
          arrow
          title={expandWindow ? 'Collapse view' : 'Expand view'}
          open={tooltipOpen}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
        >
          <IconButton
            variant='tonal'
            onClick={() => {
              onExpandToggle && onExpandToggle(!expandWindow);
              handleTooltipClose(); // Close the tooltip after the button click
            }}
            className={classes.chevronButton}
            size='small'
            sx={{ transform: 'rotate(90deg)' }}
          >
            {expandWindow ? <CloseFullscreen /> : <OpeninFullRounded />} {/* Toggle between left and right chevron */}
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', paddingTop: '8px' }}>{selects}</Box>
      <Box sx={{ flexGrow: 1 }} />
      <Stack direction='row' gap={2} alignItems='center' justifyContent='flex-end'>
        <FormControl>
          <Button variant='tonal' id='new-convo' onClick={onConvoChange}>
            New Chat
          </Button>
        </FormControl>
        <IconButton variant='tonal' onClick={onClose} className={classes.closeButton} size='large'>
          <CloseIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}

ChatHeader.propTypes = {
  options: PropTypes.array,
  onConvoChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onExpandToggle: PropTypes.func.isRequired, // Added prop type for onExpandToggle
  expandWindow: PropTypes.bool.isRequired, // Added prop type for widthValue
};
